import React from 'react';
import { motion } from 'framer-motion';
import { 
  Building2, 
  Landmark, 
  Stethoscope, 
  ShoppingBag, 
  Factory, 
  Server, 
  GraduationCap,
  ArrowRight
} from 'lucide-react';

interface IndustriesProps {
  setCurrentPage?: (page: string) => void;
}

const Industries: React.FC<IndustriesProps> = ({ setCurrentPage }) => {
  const industries = [
    {
      icon: Building2,
      name: 'Financial Services',
      description: 'Comprehensive security solutions for banks, financial institutions, and insurance companies.',
      features: [
        'Regulatory compliance (GDPR, PCI DSS)',
        'Fraud detection and prevention',
        'Secure transaction processing',
        'Data encryption and protection'
      ],
      link: 'financial-services'
    },
    {
      icon: Stethoscope,
      name: 'Healthcare',
      description: 'Advanced security measures for healthcare providers and medical facilities.',
      features: [
        'HIPAA compliance',
        'Patient data protection',
        'Medical device security',
        'Telemedicine security'
      ],
      link: 'healthcare'
    },
    {
      icon: Landmark,
      name: 'Government',
      description: 'Specialized security solutions for government agencies and public sector organizations.',
      features: [
        'Critical infrastructure protection',
        'Classified data security',
        'Cyber threat intelligence',
        'Emergency response systems'
      ],
      link: 'government'
    },
    {
      icon: Factory,
      name: 'Manufacturing',
      description: 'Industrial security solutions for manufacturing and production facilities.',
      features: [
        'Industrial control system security',
        'Supply chain protection',
        'IP protection',
        'OT/IT security integration'
      ],
      link: 'manufacturing'
    },
    {
      icon: ShoppingBag,
      name: 'Retail',
      description: 'Comprehensive security for retail businesses and e-commerce platforms.',
      features: [
        'POS system security',
        'E-commerce protection',
        'Customer data security',
        'Payment processing security'
      ],
      link: 'retail'
    },
    {
      icon: Server,
      name: 'Technology',
      description: 'Cutting-edge security solutions for technology companies and digital enterprises.',
      features: [
        'Cloud infrastructure security',
        'DevSecOps implementation',
        'API security',
        'Source code protection'
      ],
      link: 'technology'
    },
    {
      icon: GraduationCap,
      name: 'Education',
      description: 'Specialized security solutions for educational institutions and academic environments.',
      features: [
        'Student data protection',
        'Research security',
        'Campus network security',
        'E-learning protection'
      ],
      link: 'education'
    }
  ];

  return (
    <section className="py-32 bg-gray-50 dark:bg-gray-800 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0">
        <div className="absolute w-96 h-96 -right-48 top-0 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute w-96 h-96 -left-48 bottom-0 bg-purple-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="gradient-text">Industries We Serve</span>
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto"
          >
            Tailored cybersecurity solutions for various industries, protecting your specific needs
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {industries.map((industry, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white dark:bg-gray-900 rounded-2xl p-8 hover-card flex flex-col h-full"
            >
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                  <industry.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 dark:text-white ml-4">
                  {industry.name}
                </h3>
              </div>

              <p className="text-gray-600 dark:text-gray-300 mb-6 flex-grow">
                {industry.description}
              </p>

              <div className="space-y-3 mb-6">
                {industry.features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="flex items-center">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                    <span className="text-gray-600 dark:text-gray-300 text-sm">
                      {feature}
                    </span>
                  </div>
                ))}
              </div>

              <button 
                onClick={() => setCurrentPage && setCurrentPage(industry.link)}
                className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:opacity-90 transition-all duration-300 mt-auto flex items-center justify-center"
              >
                Learn More
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Industries;